import { apiService } from '@clarix/api-client';
import { pmsBase } from '../lib/base';
import { PMS_ENDPOINTS } from '../endpoints';
import type {
  FormAudienceExclusionBulkPayload,
  FormAudienceExclusionPayload,
  FormRunPreview,
  FormVersionSnapshot,
  FormTemplate,
  FormTemplateDetail,
  FormGateResult,
  CreateFormPayload,
  UpdateFormPayload,
} from '../types';

const base = () => `${pmsBase()}${PMS_ENDPOINTS.FORMS}`;

export const formsApi = {
  list: () => apiService.get<FormTemplate[]>(base()),

  get: (templateId: string) => apiService.get<FormTemplateDetail>(`${base()}/${templateId}`),

  /**
   * Blank create, or reuse an existing configuration.
   *
   * The contract (§6) carries the source template as a `?from=` QUERY param, not
   * a body field — sending it in the body was rejected as a missing-fields 400,
   * because the body schema is for the optional overrides only.
   */
  /**
   * Step 1's "Use existing" — copy a source form's configuration INTO this draft.
   *
   * Not the same as `create({ reuse_template_id })`, which makes a NEW form. By
   * step 1 the admin already has a draft open; filling it in is the intent, and
   * creating a second one would abandon the first as an empty stray.
   */
  /** Published versions, newest first — each carrying the config as published. */
  versions: (templateId: string) =>
    apiService.get<FormVersionSnapshot[]>(`${base()}/${templateId}/versions`),

  reuse: (templateId: string, sourceTemplateId: string) =>
    apiService.post<FormTemplateDetail>(`${base()}/${templateId}/reuse`, {
      source_template_id: sourceTemplateId,
    }),

  create: ({ reuse_template_id, ...overrides }: CreateFormPayload) =>
    apiService.post<FormTemplateDetail>(
      reuse_template_id ? `${base()}?from=${encodeURIComponent(reuse_template_id)}` : base(),
      overrides,
    ),

  update: (templateId: string, data: UpdateFormPayload) =>
    apiService.put<FormTemplateDetail>(`${base()}/${templateId}`, data),

  /** Publishing IS the approval (ruling D-02) — the server re-runs the gate. */
  publish: (templateId: string) =>
    apiService.post<FormTemplateDetail>(`${base()}/${templateId}/publish`),

  delete: (templateId: string) => apiService.delete<void>(`${base()}/${templateId}`),

  /** What still blocks publication — drives the stepper's ceiling. */
  gateCheck: (templateId: string) =>
    apiService.get<FormGateResult>(`${base()}/${templateId}/gate-check`),

  /**
   * Resolution as a dry run. Creates nothing — `/runs/:id/preview` needs a run,
   * which does not exist until publish, so it can only answer after the fact.
   */
  runPreview: (templateId: string) =>
    apiService.get<FormRunPreview>(`${base()}/${templateId}/run-preview`),

  /**
   * D-29 — take one person out of the previewed audience, or put them back.
   *
   * Returns the recomputed preview, so the table redraws from the server's own
   * answer rather than a guess the client patched in.
   */
  setAudienceExclusion: (
    templateId: string,
    membershipId: string,
    data: FormAudienceExclusionPayload,
  ) =>
    apiService.put<FormRunPreview>(
      `${base()}/${templateId}/run-preview/exclusions/${membershipId}`,
      data,
    ),

  /**
   * The same decision for a selection, in ONE call.
   *
   * Not N single calls: each returns the whole recomputed preview, so the last
   * response to land would win the cache, and a partial failure would leave the
   * table half-right with nothing saying which half.
   */
  setAudienceExclusions: (templateId: string, data: FormAudienceExclusionBulkPayload) =>
    apiService.put<FormRunPreview>(`${base()}/${templateId}/run-preview/exclusions`, data),
};
